import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { createTeam, checkSlugAvailable } from '../lib/supabase';

const PLANS = [
  {
    id: 'starter', label: 'Starter', price: '$29/mo', seats: '5 seats',
    features: ['5 seat pool across all teams', 'Shared prompt library', 'Team folders', 'Team Skills'],
    color: '#7f77dd',
  },
  {
    id: 'growth', label: 'Growth', price: '$79/mo', seats: '15 seats',
    features: ['15 seat pool across all teams', 'Everything in Starter', 'Approval workflow', 'Usage analytics'],
    color: '#1d9e75',
    popular: true,
  },
  {
    id: 'agency', label: 'Agency', price: '$149/mo', seats: 'Unlimited seats',
    features: ['Unlimited seat pool', 'Everything in Growth', 'Priority support', 'Custom onboarding'],
    color: '#ef9f27',
  },
];

export default function CreateTeamPage() {
  const { user, profile, hasTeamsPlan, teamsPlan, teamsSeatLimit } = useAuth();
  const { loadTeams, switchTeam, teams } = useTeam();
  const navigate = useNavigate();

  // If user has a teams plan → just name the team (1 step)
  // If no teams plan → subscribe first (2 steps: plan → name)
  // Start on 'name' if hasTeamsPlan, otherwise 'plan'. useEffect corrects this after profile loads.
  const [step, setStep]   = useState('plan'); // will be corrected by useEffect once profile loads
  const [form, setForm]   = useState({ name: '', slug: '' });
  const [plan, setPlan]   = useState('starter');
  const [slugOk, setSlugOk]     = useState(true);
  const [slugChecking, setSlugChecking] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [newTeam, setNewTeam]   = useState(null);

  const wasCanceled   = new URLSearchParams(window.location.search).get('canceled') === 'true';
  const wasSubscribed = new URLSearchParams(window.location.search).get('subscribed') === '1';

  // After returning from Stripe checkout, refresh profile and advance to name step
  useEffect(() => {
    if (wasSubscribed) {
      window.history.replaceState({}, '', '/team/create');
    }
  }, []);

  // Set correct starting step once profile loads
  // useState initial value runs before profile is available so we can't trust it
  useEffect(() => {
    if (hasTeamsPlan) {
      if (step === 'plan') setStep('name');
    }
  }, [hasTeamsPlan]);

  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (name) => {
    setV('name', name);
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setV('slug', slug);
    if (slug) {
      setSlugChecking(true);
      clearTimeout(window._slugTimer);
      window._slugTimer = setTimeout(async () => {
        const ok = await checkSlugAvailable(slug).catch(() => false);
        setSlugOk(ok);
        setSlugChecking(false);
      }, 500);
    }
  };

  // ── Path A: User has teams plan → create team directly ───────
  const handleCreate = async () => {
    setSaving(true);
    try {
      const team = await createTeam({ name: form.name.trim(), slug: form.slug, plan: teamsPlan || 'starter' });
      await loadTeams();
      await switchTeam(team.id);
      setNewTeam(team);
      setStep('done');
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  // ── Path B: No teams plan → subscribe via Stripe ─────────────
  const handleSubscribe = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/team-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId:           plan,
          userId:           user.id,
          userEmail:        user.email,
          stripeCustomerId: profile?.stripe_customer_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      // Upgraded in place (had Pro sub)
      if (data.success) {
        await loadTeams();
        setStep('name');
        return;
      }
      // New checkout — redirect to Stripe
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const PLAN_INFO = { starter: 'Starter', growth: 'Growth', agency: 'Agency' };
  const stepNum = hasTeamsPlan ? { name: 1, done: 2 } : { plan: 1, name: 2, done: 3 };
  const totalSteps = hasTeamsPlan ? 2 : 3;

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
        </div>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 28, justifyContent: 'center' }}>
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map(n => (
            <div key={n} style={{
              width: n === stepNum[step] ? 24 : 8, height: 8, borderRadius: 4,
              background: n <= stepNum[step] ? '#7f77dd' : 'var(--border2)',
              transition: 'all 0.2s',
            }} />
          ))}
        </div>

        {/* ── Step: Plan selection (no teams plan yet) ── */}
        {step === 'plan' && (
          <>
            <div style={s.stepTitle}>Choose a Teams plan</div>
            <div style={s.stepSub}>
              One subscription gives you a seat pool to use across unlimited teams.
              {wasCanceled && <span style={{ color: 'var(--danger)', marginLeft: 6 }}>Checkout was canceled.</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {PLANS.map(p => (
                <div key={p.id} onClick={() => setPlan(p.id)} style={{
                  border: `${plan === p.id ? '1.5px' : '0.5px'} solid ${plan === p.id ? p.color + '80' : 'var(--border2)'}`,
                  background: plan === p.id ? `${p.color}10` : 'var(--surface2)',
                  borderRadius: 'var(--radius)', padding: '14px 16px',
                  cursor: 'pointer', transition: 'all 0.12s', position: 'relative',
                }}>
                  {p.popular && (
                    <span style={{
                      position: 'absolute', top: -9, right: 14,
                      background: p.color, color: '#fff', fontSize: 10,
                      padding: '2px 10px', borderRadius: 10, fontWeight: 500,
                    }}>Most popular</span>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{p.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--dim)', marginLeft: 8 }}>{p.seats}</span>
                    </div>
                    <span style={{ fontSize: 18, fontWeight: 500, color: p.color }}>{p.price}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    {p.features.map(f => (
                      <span key={f} style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ color: p.color }}>✓</span> {f}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate(-1)} style={s.cancelBtn}>← Cancel</button>
              <button onClick={handleSubscribe} disabled={saving} style={{ ...s.primaryBtn, flex: 1, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Setting up...' : 'Subscribe & continue →'}
              </button>
            </div>
          </>
        )}

        {/* ── Step: Name the team ── */}
        {step === 'name' && (
          <>
            <div style={s.stepTitle}>Name your team</div>
            <div style={s.stepSub}>
              {hasTeamsPlan
                ? <>You're on <strong style={{ color: '#7f77dd' }}>{PLAN_INFO[teamsPlan]} ({teamsSeatLimit === 999 ? 'unlimited' : teamsSeatLimit} seats)</strong>. Create as many teams as you need.</>
                : 'This is how your team will appear across CopyPasteGenius.'}
            </div>

            <div style={s.field}>
              <label style={s.label}>Team name</label>
              <input
                style={s.input}
                placeholder="Acme Creative Agency"
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                autoFocus
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>
                Team slug
                <span style={{ fontSize: 10, color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>auto-generated</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{
                    ...s.input,
                    borderColor: !slugOk ? 'rgba(226,75,74,0.6)' : form.slug && slugOk ? 'rgba(29,158,117,0.4)' : undefined,
                  }}
                  value={form.slug}
                  onChange={e => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
                    setV('slug', v);
                  }}
                />
                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: slugOk ? '#5dcaa5' : 'var(--danger)' }}>
                  {slugChecking ? '…' : !slugOk ? '✗ Taken' : form.slug ? '✓' : ''}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              {!hasTeamsPlan && <button onClick={() => setStep('plan')} style={s.cancelBtn}>← Back</button>}
              {hasTeamsPlan && <button onClick={() => navigate(-1)} style={s.cancelBtn}>← Cancel</button>}
              <button
                onClick={handleCreate}
                disabled={!form.name.trim() || !slugOk || slugChecking || saving}
                style={{ ...s.primaryBtn, flex: 1, opacity: (!form.name.trim() || !slugOk || slugChecking || saving) ? 0.5 : 1 }}>
                {saving ? 'Creating...' : 'Create team →'}
              </button>
            </div>
          </>
        )}

        {/* ── Step: Done ── */}
        {step === 'done' && newTeam && (
          <>
            <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>🎉</div>
            <div style={{ ...s.stepTitle, textAlign: 'center' }}>{newTeam.name} is live!</div>
            <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, textAlign: 'center', marginBottom: 24 }}>
              Your team workspace is ready. Invite your first members to get started.
            </div>
            {hasTeamsPlan && (
              <div style={{ fontSize: 12, color: 'var(--dim)', textAlign: 'center', marginBottom: 16 }}>
                You can create more teams anytime from the sidebar.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={() => navigate('/team')} style={s.primaryBtn}>Go to team dashboard →</button>
              <button onClick={() => { setStep('name'); setForm({ name: '', slug: '' }); setNewTeam(null); }} style={s.cancelBtn}>
                + Create another team
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:      { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'var(--font)' },
  card:      { background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 16, padding: '36px 32px', maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', gap: 14 },
  logo:      { fontSize: 18, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.03em', marginBottom: 4 },
  stepTitle: { fontSize: 20, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' },
  stepSub:   { fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: -6 },
  field:     { display: 'flex', flexDirection: 'column', gap: 5 },
  label:     { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  input:     { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%' },
  primaryBtn:{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '11px', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer', width: '100%' },
  cancelBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '10px 16px', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' },
};